import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

let fileBaseUrl: string | null = null;
let fetchPromise: Promise<void> | null = null;

/**
 * Fetches the file base URL from the backend config endpoint.
 * Called once on first use; subsequent calls return the cached value.
 */
async function ensureFileBaseUrl(): Promise<string> {
  if (fileBaseUrl !== null) return fileBaseUrl;

  if (!fetchPromise) {
    fetchPromise = axios
      .get(`${API_BASE_URL}/config`)
      .then((res) => {
        fileBaseUrl = res.data.fileBaseUrl || '/uploads';
      })
      .catch(() => {
        // Fallback to /uploads if config endpoint fails
        fileBaseUrl = '/uploads';
      });
  }

  await fetchPromise;
  return fileBaseUrl!;
}

/**
 * Resolve a storage key to a full file URL.
 * Uses the cached file base URL (CDN URL for R2, /uploads for local).
 * If the config hasn't loaded yet, falls back to /uploads.
 */
export function resolveFileUrl(key: string): string {
  const base = fileBaseUrl || '/uploads';
  if (base.startsWith('http')) {
    // Absolute CDN URL — encode each path segment but preserve slashes
    const encodedKey = key.split('/').map(encodeURIComponent).join('/');
    return `${base}/${encodedKey}`;
  }
  // Relative path (local storage or API proxy)
  return `${base}/${key}`;
}

/**
 * Initialize the file URL config. Call this early in app startup.
 */
export async function initFileUrl(): Promise<void> {
  await ensureFileBaseUrl();
}

/**
 * Get the current file base URL (may be null if not yet loaded).
 */
export function getFileBaseUrl(): string {
  return fileBaseUrl || '/uploads';
}
